/**
 * Hybrid Persistence Adapter
 * 
 * Always-on localStorage persistence with optional database extension.
 * This provides the best user experience:
 * - Immediate saves to localStorage (never lose work)
 * - Background sync to database (if available)
 * - Automatic fallback when database fails
 * - Cross-session persistence guaranteed
 */

import type { WizardData } from '../types/WizardTypes';
import type { WizardDraft, PersistenceService } from '../stores/useWizardPersistence';
import { LocalStoragePersistenceAdapter } from './LocalStoragePersistenceAdapter';

export interface DatabasePersistenceService<TData extends WizardData> {
  saveDraft(draft: Omit<WizardDraft<TData>, 'id' | 'createdAt' | 'updatedAt'>): Promise<string>;
  loadDraft(draftId: string): Promise<WizardDraft<TData> | null>;
  deleteDraft(draftId: string): Promise<boolean>;
  listDrafts(wizardId: string): Promise<WizardDraft<TData>[]>;
  syncDraft(localDraft: WizardDraft<TData>): Promise<void>;
}

export class HybridPersistenceAdapter<TData extends WizardData> implements PersistenceService<TData> {
  private readonly localStorage: LocalStoragePersistenceAdapter<TData>;
  private readonly database?: DatabasePersistenceService<TData>;
  private readonly enableBackgroundSync: boolean;
  private syncQueue: Set<string> = new Set();
  private syncInProgress = false;

  constructor(
    keyPrefix = 'wizard-draft',
    database?: DatabasePersistenceService<TData>,
    options: {
      enableBackgroundSync?: boolean;
      syncIntervalMs?: number;
    } = {}
  ) {
    this.localStorage = new LocalStoragePersistenceAdapter<TData>(keyPrefix);
    this.database = database;
    this.enableBackgroundSync = options.enableBackgroundSync ?? true;

    // Set up background sync if enabled
    if (this.enableBackgroundSync && this.database) {
      this.startBackgroundSync(options.syncIntervalMs || 60000); // Default 1 minute
    }
  }

  async saveDraft(draft: Omit<WizardDraft<TData>, 'id' | 'createdAt' | 'updatedAt'> | WizardDraft<TData>): Promise<string> {
    // If database is available, try immediate sync first to get any updates (like quoteId)
    if (this.database) {
      try {
        const dbDraftId = await this.database.saveDraft(draft);
        
        // Save the potentially updated data to localStorage
        const updatedDraftId = await this.localStorage.saveDraft(draft);
        
        return dbDraftId || updatedDraftId;
      } catch (error) {
        console.warn('Database sync failed, falling back to localStorage + background sync:', error);
        
        // Fallback to original behavior: localStorage first, then background sync
        const draftId = await this.localStorage.saveDraft(draft);
        this.syncQueue.add(draftId);
        
        // Try background sync (non-blocking)
        this.tryBackgroundSync().catch(bgError => {
          console.warn('Background sync failed, will retry later:', bgError);
        });
        
        return draftId;
      }
    } else {
      // No database available, just save to localStorage
      return await this.localStorage.saveDraft(draft);
    }
  }

  async loadDraft(draftId: string): Promise<WizardDraft<TData> | null> {
    // Try database first (most up-to-date), then localStorage fallback
    if (this.database) {
      try {
        const dbDraft = await this.database.loadDraft(draftId);
        if (dbDraft) {
          // Update localStorage with latest from database
          await this.localStorage.saveDraft({
            wizardId: dbDraft.wizardId,
            data: dbDraft.data,
            metadata: dbDraft.metadata,
          });
          return dbDraft;
        }
      } catch (error) {
        console.warn('Database load failed, falling back to localStorage:', error);
      }
    }
    
    // Fallback to localStorage
    return this.localStorage.loadDraft(draftId);
  }

  async deleteDraft(draftId: string): Promise<boolean> {
    let success = true;
    
    // Delete from localStorage
    const localSuccess = await this.localStorage.deleteDraft(draftId);
    if (!localSuccess) success = false;
    
    // Delete from database if available
    if (this.database) {
      try {
        const dbSuccess = await this.database.deleteDraft(draftId);
        if (!dbSuccess) success = false;
      } catch (error) {
        console.warn('Database delete failed:', error);
        success = false;
      }
    }
    
    // Remove from sync queue
    this.syncQueue.delete(draftId);
    
    return success;
  }

  async listDrafts(wizardId: string): Promise<WizardDraft<TData>[]> {
    const localDrafts = await this.localStorage.listDrafts(wizardId);
    
    if (!this.database) {
      return localDrafts;
    }
    
    try {
      // Get database drafts
      const dbDrafts = await this.database.listDrafts(wizardId);
      
      // Merge and deduplicate (database takes precedence for same ID)
      const draftMap = new Map<string, WizardDraft<TData>>();
      
      // Add local drafts first
      localDrafts.forEach(draft => draftMap.set(draft.id, draft));
      
      // Override with database drafts (more recent)
      dbDrafts.forEach(draft => draftMap.set(draft.id, draft));
      
      const mergedDrafts = Array.from(draftMap.values());
      
      // Sort by updatedAt descending
      return mergedDrafts.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      
    } catch (error) {
      console.warn('Database list failed, returning localStorage drafts:', error);
      return localDrafts;
    }
  }

  /**
   * Force sync all pending drafts to database
   */
  async forceSyncAll(): Promise<{ synced: number; failed: string[] }> {
    if (!this.database) {
      return { synced: 0, failed: [] };
    }

    const results = { synced: 0, failed: [] as string[] };
    
    for (const draftId of this.syncQueue) {
      try {
        const draft = await this.localStorage.loadDraft(draftId);
        if (draft) {
          await this.database.syncDraft(draft);
          this.syncQueue.delete(draftId);
          results.synced++;
        }
      } catch (error) {
        console.error(`Failed to sync draft ${draftId}:`, error);
        results.failed.push(draftId);
      }
    }
    
    return results;
  }

  /**
   * Get sync status
   */
  getSyncStatus(): {
    pendingSync: number;
    databaseAvailable: boolean;
    lastSyncAttempt?: Date;
  } {
    return {
      pendingSync: this.syncQueue.size,
      databaseAvailable: !!this.database,
      // Could add lastSyncAttempt tracking
    };
  }

  /**
   * Background sync implementation
   */
  private startBackgroundSync(intervalMs: number): void {
    setInterval(() => {
      if (!this.syncInProgress && this.syncQueue.size > 0) {
        this.tryBackgroundSync().catch(error => {
          console.warn('Background sync failed:', error);
        });
      }
    }, intervalMs);
  }

  private async tryBackgroundSync(): Promise<void> {
    if (!this.database || this.syncInProgress || this.syncQueue.size === 0) {
      return;
    }

    this.syncInProgress = true;
    
    try {
      // Sync up to 5 drafts per attempt to avoid blocking
      const toSync = Array.from(this.syncQueue).slice(0, 5);
      
      for (const draftId of toSync) {
        try {
          const draft = await this.localStorage.loadDraft(draftId);
          if (draft) {
            await this.database.syncDraft(draft);
            this.syncQueue.delete(draftId);
          }
        } catch (error) {
          console.warn(`Failed to sync draft ${draftId}, will retry later:`, error);
          // Keep in queue for retry
        }
      }
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Clear all data (localStorage + database)
   */
  async clearAll(wizardId: string): Promise<void> {
    // Clear localStorage
    await this.localStorage.clearWizardDrafts(wizardId);
    
    // Clear database if available
    if (this.database) {
      try {
        const drafts = await this.database.listDrafts(wizardId);
        await Promise.all(drafts.map(draft => this.database!.deleteDraft(draft.id)));
      } catch (error) {
        console.warn('Failed to clear database drafts:', error);
      }
    }
    
    // Clear sync queue
    this.syncQueue.clear();
  }
}
